/**
 * Renderer-side vision-capability adapter over the shared model catalog.
 *
 * The vision flags + Ollama family patterns live in ONE place —
 * `packages/shared/src/models.ts` (`modelSupportsVision`). The renderer can import that
 * runtime function directly (it already imports `getModelSpec`, `effortLevelsFor`,
 * etc. from `@onestone/shared/models`), so there is no separate mirrored table here.
 *
 * The only behavioural difference the UI needs: where the shared function
 * collapses "unknown model" to `false`, the renderer wants `null` so the
 * composer stays silent (the backend gates image input anyway) instead of
 * showing a false "this model can't see images" warning on an unrecognised id
 * (OpenRouter SKUs, freshly-pulled Ollama tags we don't pattern-match, etc.).
 *
 * So: catalog hit (exact or prefix) or Ollama → defer to the shared boolean;
 * genuinely unknown → `null`.
 */
import { matchModelSpec, modelSupportsVision as catalogVision } from '@onestone/shared/models';

/**
 * Whether the given model accepts image input.
 *  - Catalog hit on that provider (exact or a snapshot) → true/false from the shared catalog.
 *  - Ollama provider → true/false from the shared family patterns.
 *  - Empty / unknown model → `null` (caller stays silent; backend gates).
 */
export function modelSupportsVision(modelId: string, provider: string): boolean | null {
  if (!modelId) return null;
  // Ollama ids live outside the catalog; the shared function pattern-matches
  // them to a definitive boolean, so it's always meaningful for that provider.
  if (provider === 'ollama') return catalogVision(modelId, provider);
  // Known catalog model on that provider (exact id or a snapshot of one) →
  // the shared boolean is authoritative.
  return matchModelSpec(modelId, provider) ? catalogVision(modelId, provider) : null;
}
